import { Mui, TOC } from "@osu/react-ui";
import { useGetScholarsApplicationDetailsQuery, useGetScholarsApplicantQueueEntriesQuery } from "../../services/graphql/api";
import scholarsSections from "./scholarsSections";
import useRoleAuthentication from "../../Authentication/hooks/useRoleAuthentication";
import "./tableofcontents.css";

/**
 * has507 — Scholars-specific applicant details page.
 * Separate from the shared Applicant component to prevent regressions on the Honors side
 * and allow scholars-tailored sections without conditional hacks.
 */
function ScholarsApplicant() {
    const params = getScholarsApplDetailParams();
    const { error, isLoading, isFetching } = useGetScholarsApplicationDetailsQuery(params, {
        skip: !params?.applicantEmployeeId || !params?.term,
        refetchOnMountOrArgChange: true
    });
    // Scholars review is QUEUE/scholars program based, not person-assignment based: an applicant is worked
    // via a program queue entry (QUEUED -> IN_REVIEW -> RECOMMENDED), not assigned to a specific reviewer.
    // So the review-scoped sections (Ratings) are gated on an active (IN_REVIEW) queue entry — the same
    // signal ScholarsRecommendation uses to show the live decision panel — NOT on a person match.
    const { data: queueEntries } = useGetScholarsApplicantQueueEntriesQuery(
        { applicantEmployeeId: params?.applicantEmployeeId, term: params?.term },
        { skip: !params?.applicantEmployeeId }
    );
    const authDetails = useRoleAuthentication({ autoMode: true, redirectOnUnauthorized: true });

    if (isLoading) return <LoadingArea />;
    if (error) return <Mui.Typography>Error: {error.message}</Mui.Typography>;

    const opacity = isFetching ? "0.5" : "default";
    const filterFlags = {
        isAdmin: authDetails?.isScholarsAdmin,
        // True when the applicant is actively in review in a program queue (queue-based, per the Scholars model).
        isInReview: (queueEntries ?? []).some(entry => entry?.status === "IN_REVIEW"),
        // True once a recommendation has been submitted (entry RECOMMENDED) — drives the read-only Ratings view.
        isRecommended: (queueEntries ?? []).some(entry => entry?.status === "RECOMMENDED"),
    };

    return scholarsSections
        .filter(section => section.authorization?.some(key => authDetails?.[key] === true) && (!section.customFilter || section.customFilter(filterFlags)))
        .map(
            (
                { id, heading, sortPriority, Component: ChildComponent, defaultExpanded },
                index
            ) => (
                <TOC.Section
                    expandable
                    defaultExpanded={defaultExpanded ?? true}
                    sx={{ marginBottom: "1rem", opacity }}
                    id={id}
                    key={`${index}${id}-jumpsection`}
                    heading={heading}
                    sortPriority={sortPriority}
                    AnchorHeadingProps={{ variant: "h5", component: "h2" }}
                >
                    <ChildComponent />
                </TOC.Section>
            )
        );
}

/**
 * Extract applicant params from the scholars-specific URL pattern:
 * /scholars/applicant/:applicantEmployeeId/:term
 * programId is always "scholars" for this route.
 */
export function getScholarsApplDetailParams() {
    // Shape-aware: ONLY the /scholars/applicant/{applicantEmployeeId}/{term} URL yields params. Other
    // Scholars pages (notably the queue at /scholars/queue/{subProgramId}) must return empty so callers'
    // `skip: !applicantEmployeeId` guards don't fire applicant-scoped queries with a non-applicant segment.
    // (Previously used indexOf('scholars')+2, which on /scholars/queue/HUM returned applicantEmployeeId="HUM"
    // — a scholars program code — and hit resolveApplicantTerm, producing the "No Scholars records found for
    // applicant HUM" banner after a deny redirect. has561 bugfix.)
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'scholars' && parts[1] === 'applicant') {
        return { applicantEmployeeId: parts[2], programId: "scholars", term: parts[3] };
    }
    return { applicantEmployeeId: undefined, programId: "scholars", term: undefined };
}

function LoadingArea() {
    return (
        <Mui.Box>
            <Mui.Skeleton variant="rect" height="64px" /><br />
            <Mui.Skeleton variant="rect" height="64px" /><br />
            <Mui.Skeleton variant="rect" height="64px" /><br />
            <Mui.Skeleton variant="rect" height="64px" /><br />
            <Mui.Skeleton variant="rect" height="64px" /><br />
            <Mui.Skeleton variant="rect" width={100} height="1em" />
        </Mui.Box>
    );
}

export default ScholarsApplicant;
